import {
    ZfCard,
    ZfCardMedia,
    ZfCardMediaHeader,
    ZfDialog,
    ZfDialogContent,
    ZfDialogFooter,
    ZfDialogHeader,
    ZfStack,
    ZfTextButton,
    ZfTypography,
} from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrlByString } from '../../../../styles/assets/imageMap';
import { useActiveDomicile, useContentByTalkingPointIdAndDomicile } from '../../../api';
import type { AdvisoryNeedDto, TalkingPointDto } from '../../../api/generated/client';
import type { IThinTopic } from '../../../interfaces/topic';
import { List } from '../../atoms';

interface IProps {
    isOpen: boolean;
    topics: IThinTopic[];
    selectedTopic?: IThinTopic;
    selectedTalkingPoint?: TalkingPointDto;
    onTopicSelected: (topic?: IThinTopic) => void;
    onNeedSelected: (need?: AdvisoryNeedDto) => void;
    onClose: () => void;
    onBack: () => void;
}

export const SelectNeedModal: FunctionComponent<IProps> = ({
    isOpen,
    topics,
    selectedTopic: initialTopic,
    selectedTalkingPoint: initialTalkingPoint,
    onTopicSelected,
    onNeedSelected,
    onClose,
    onBack,
}) => {
    const { t, i18n } = useTranslation();
    const [selectedTopic, setSelectedTopic] = useState<IThinTopic | undefined>(initialTopic);
    const [selectedTalkingPoint] = useState<TalkingPointDto | undefined>(initialTalkingPoint);
    const [selectedNeed, setSelectedNeed] = useState<AdvisoryNeedDto | undefined>(undefined);
    const [selectedNeedIndex, setSelectedNeedIndex] = useState<number>();

    const activeDomicile = useActiveDomicile();
    const { data: advisoryNeedsForTalkingPoint } = useContentByTalkingPointIdAndDomicile(initialTalkingPoint?.id, activeDomicile);

    const [advisoryNeeds, setAdvisoryNeeds] = useState(advisoryNeedsForTalkingPoint);

    const onTopicClick = (topic: IThinTopic, selected: boolean) => {
        const topic_ = selected ? topic : undefined;
        setSelectedTopic(topic_);
        setSelectedNeed(undefined);
        setSelectedNeedIndex(-1);
        setAdvisoryNeeds([]);
        onTopicSelected(topic_);
    };

    const onNeedClick = (advisoryNeed: AdvisoryNeedDto, index: number) => {
        if (selectedNeedIndex === index) {
            setSelectedNeedIndex(undefined);
            setSelectedNeed(undefined);
        } else {
            setSelectedNeedIndex(index);
            setSelectedNeed(advisoryNeed);
        }
    };

    const onDialogConfirm = () => onNeedSelected(selectedNeed);

    useEffect(() => {
        setAdvisoryNeeds(advisoryNeedsForTalkingPoint);
    }, [advisoryNeedsForTalkingPoint]);

    return (
        <ZfDialog show={isOpen} onDialogClose={onClose} size="m" className="select-topic-modal">
            <ZfDialogHeader>{t('zfEventModalNewNeed')}</ZfDialogHeader>
            <ZfDialogContent>
                <ZfStack direction="row" spacing="extra-large">
                    <ZfStack direction="column" spacing="extra-small" style={{ maxHeight: '60vh', overflowY: 'scroll', overflowX: 'hidden' }}>
                        {topics.map((topic) => (
                            <ZfCard
                                key={topic.id}
                                className="action-dialog-selectable-card"
                                onCardSelect={(event: CustomEvent<boolean>) => onTopicClick(topic, event.detail)}
                                selectable={true}
                                selected={selectedTopic?.id === topic.id}
                            >
                                <ZfCardMedia image={getImageUrlByString(topic.key)} style={{ height: '140px' }}>
                                    <ZfCardMediaHeader>
                                        <ZfTypography slot="leading" variant="body-large" color="invert">
                                            {topic.name[i18n.language]}
                                        </ZfTypography>
                                    </ZfCardMediaHeader>
                                </ZfCardMedia>
                            </ZfCard>
                        ))}
                    </ZfStack>
                    <ZfStack direction="column" style={{ flex: 1 }}>
                        {!!selectedTopic && !!selectedTalkingPoint && !!advisoryNeeds && advisoryNeeds.length > 0 && (
                            <List
                                title={selectedTalkingPoint?.name[i18n.language]}
                                items={advisoryNeeds.map((advisoryNeed) => ({
                                    title: advisoryNeed.name[i18n.language],
                                    data: advisoryNeed,
                                }))}
                                selectedIndex={selectedNeedIndex}
                                isLoading={!advisoryNeeds}
                                onItemClick={onNeedClick}
                                hasBorderBottom={(index) => index < advisoryNeeds.length - 1}
                            />
                        )}
                    </ZfStack>
                </ZfStack>
            </ZfDialogContent>
            <ZfDialogFooter>
                <ZfStack slot="primary" direction="row" spacing="small">
                    <ZfTextButton hierarchy="tertiary" size="medium" onClick={onBack}>
                        {t('back')}
                    </ZfTextButton>
                    <ZfTextButton disabled={!selectedNeed} size="medium" onClick={onDialogConfirm}>
                        {t('eventModalNextButton')}
                    </ZfTextButton>
                </ZfStack>
            </ZfDialogFooter>
        </ZfDialog>
    );
};
